/**
 * GalleryAnalytics - Dashboard for tracking gallery/landing page performance
 * Shows views, contacts, conversion rates, device breakdown, and referrer sources
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    BarChart3, TrendingUp, Users, MousePointerClick,
    Eye, Mail, Smartphone, Monitor, Tablet, Bot,
    Globe, RefreshCw, Loader2, Calendar, ChevronDown,
    ExternalLink, ArrowUpRight, ArrowDownRight
} from 'lucide-react';
import { fetchWithAuth, BASE_URL } from '../api/client';

// Date range options
const DATE_RANGES = [
    { label: 'Last 7 days', value: 7 },
    { label: 'Last 30 days', value: 30 },
    { label: 'Last 90 days', value: 90 }
];

// Device icon mapping
const DEVICE_ICONS = {
    desktop: Monitor,
    mobile: Smartphone,
    tablet: Tablet,
    bot: Bot
};

// Stat card component
const StatCard = ({ title, value, icon: Icon, change, changeType, subtitle }) => (
    <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700">
        <div className="flex items-start justify-between">
            <div>
                <p className="text-sm text-slate-500 dark:text-slate-400">{title}</p>
                <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">{value}</p>
                {subtitle && (
                    <p className="text-xs text-slate-400 dark:text-slate-500 mt-1">{subtitle}</p>
                )}
            </div>
            <div className={`p-3 rounded-xl ${
                changeType === 'positive' ? 'bg-emerald-50 dark:bg-emerald-900/20' :
                changeType === 'negative' ? 'bg-red-50 dark:bg-red-900/20' :
                'bg-slate-100 dark:bg-slate-700'
            }`}>
                <Icon className={`w-5 h-5 ${
                    changeType === 'positive' ? 'text-emerald-600 dark:text-emerald-400' :
                    changeType === 'negative' ? 'text-red-600 dark:text-red-400' :
                    'text-slate-600 dark:text-slate-400'
                }`} />
            </div>
        </div>
        {change !== undefined && (
            <div className={`flex items-center gap-1 mt-3 text-xs font-medium ${
                change >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'
            }`}>
                {change >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                {Math.abs(change)}% vs previous period
            </div>
        )}
    </div>
);

// Simple bar chart for daily views
const DailyChart = ({ data }) => {
    if (!data || data.length === 0) return null;

    const maxViews = Math.max(...data.map(d => d.views || 0), 1);

    return (
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">Daily Views</h3>
            <div className="flex items-end gap-1 h-32">
                {data.slice(-14).map((day, idx) => (
                    <div key={idx} className="flex-1 flex flex-col items-center gap-1">
                        <div
                            className="w-full bg-emerald-500 dark:bg-emerald-400 rounded-t transition-all hover:bg-emerald-600"
                            style={{ height: `${(day.views / maxViews) * 100}%`, minHeight: day.views > 0 ? '4px' : '0' }}
                            title={`${day.date}: ${day.views} views`}
                        />
                        {idx % 2 === 0 && (
                            <span className="text-[10px] text-slate-400 dark:text-slate-500">
                                {new Date(day.date).getDate()}
                            </span>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

// Device breakdown component
const DeviceBreakdown = ({ data }) => {
    if (!data) return null;

    const total = Object.values(data).reduce((sum, val) => sum + val, 0) || 1;
    const devices = ['desktop', 'mobile', 'tablet', 'bot'].filter(d => data[d] > 0);

    return (
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">Traffic by Device</h3>
            <div className="space-y-3">
                {devices.map(device => {
                    const Icon = DEVICE_ICONS[device];
                    const count = data[device] || 0;
                    const percent = ((count / total) * 100).toFixed(1);

                    return (
                        <div key={device} className="flex items-center gap-3">
                            <Icon className="w-4 h-4 text-slate-400" />
                            <div className="flex-1">
                                <div className="flex items-center justify-between text-sm mb-1">
                                    <span className="text-slate-700 dark:text-slate-300 capitalize">{device}</span>
                                    <span className="text-slate-500 dark:text-slate-400">{count} ({percent}%)</span>
                                </div>
                                <div className="h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-emerald-500 dark:bg-emerald-400 rounded-full transition-all"
                                        style={{ width: `${percent}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Referrer breakdown component
const ReferrerBreakdown = ({ data }) => {
    if (!data) return null;

    const referrers = Object.entries(data)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5);

    const total = Object.values(data).reduce((sum, val) => sum + val, 0) || 1;

    return (
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">Top Referrers</h3>
            <div className="space-y-3">
                {referrers.map(([referrer, count]) => {
                    const percent = ((count / total) * 100).toFixed(1);

                    return (
                        <div key={referrer} className="flex items-center gap-3">
                            <Globe className="w-4 h-4 text-slate-400" />
                            <div className="flex-1">
                                <div className="flex items-center justify-between text-sm mb-1">
                                    <span className="text-slate-700 dark:text-slate-300">{referrer}</span>
                                    <span className="text-slate-500 dark:text-slate-400">{count} ({percent}%)</span>
                                </div>
                                <div className="h-1.5 bg-slate-100 dark:bg-slate-700 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-500 dark:bg-blue-400 rounded-full transition-all"
                                        style={{ width: `${percent}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

// Top galleries table
const TopGalleries = ({ galleries, onViewGallery }) => {
    if (!galleries || galleries.length === 0) return null;

    return (
        <div className="bg-white dark:bg-slate-800 rounded-2xl p-5 border border-slate-200 dark:border-slate-700">
            <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-4">Top Performing Galleries</h3>
            <div className="space-y-2">
                {galleries.slice(0, 5).map((gallery, idx) => (
                    <div
                        key={gallery.galleryId}
                        className="flex items-center justify-between p-3 rounded-xl bg-slate-50 dark:bg-slate-700/50 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                        onClick={() => onViewGallery && onViewGallery(gallery.galleryId)}
                    >
                        <div className="flex items-center gap-3">
                            <span className="text-sm font-medium text-slate-400 dark:text-slate-500 w-5">
                                #{idx + 1}
                            </span>
                            <div>
                                <p className="text-sm font-medium text-slate-700 dark:text-slate-300 truncate max-w-[200px]">
                                    {gallery.address || gallery.galleryId}
                                </p>
                                <div className="flex items-center gap-3 mt-0.5">
                                    <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                        <Eye className="w-3 h-3" /> {gallery.views}
                                    </span>
                                    <span className="text-xs text-slate-500 dark:text-slate-400 flex items-center gap-1">
                                        <Mail className="w-3 h-3" /> {gallery.contacts}
                                    </span>
                                </div>
                            </div>
                        </div>
                        <ExternalLink className="w-4 h-4 text-slate-400" />
                    </div>
                ))}
            </div>
        </div>
    );
};

// Main GalleryAnalytics component
const GalleryAnalytics = () => {
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [days, setDays] = useState(30);
    const [showDaysDropdown, setShowDaysDropdown] = useState(false);
    const [analytics, setAnalytics] = useState(null);

    const fetchAnalytics = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetchWithAuth(
                `${BASE_URL}/api/staging/galleries/analytics/summary?days=${days}`
            );

            if (!response.ok) {
                throw new Error('Failed to fetch analytics');
            }

            const data = await response.json();
            setAnalytics(data);
        } catch (err) {
            console.error('[GalleryAnalytics] Error:', err);
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [days]);

    useEffect(() => {
        fetchAnalytics();
    }, [fetchAnalytics]);

    const handleViewGallery = (galleryId) => {
        // Open gallery in new tab
        const baseUrl = window.location.hostname === 'localhost'
            ? 'http://localhost:3000'
            : 'https://cloudmortgage.io';
        window.open(`${baseUrl}/flow/${galleryId}`, '_blank');
    };

    if (loading) {
        return (
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="flex flex-col items-center gap-3">
                    <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
                    <p className="text-sm text-slate-500 dark:text-slate-400">Loading analytics...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex-1 flex items-center justify-center p-8">
                <div className="text-center">
                    <BarChart3 className="w-12 h-12 text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                    <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">{error}</p>
                    <button
                        onClick={fetchAnalytics}
                        className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    const { summary, topGalleries, daily, byDevice, byReferrer } = analytics || {};

    return (
        <div className="flex-1 flex flex-col overflow-auto p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-xl font-bold text-slate-900 dark:text-white">Gallery Analytics</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
                        Track views and leads across all your property galleries
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    {/* Date range selector */}
                    <div className="relative">
                        <button
                            onClick={() => setShowDaysDropdown(!showDaysDropdown)}
                            className="flex items-center gap-2 px-4 py-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        >
                            <Calendar className="w-4 h-4" />
                            {DATE_RANGES.find(r => r.value === days)?.label}
                            <ChevronDown className="w-4 h-4" />
                        </button>
                        {showDaysDropdown && (
                            <div className="absolute right-0 mt-2 w-40 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl shadow-lg z-10 py-1">
                                {DATE_RANGES.map(range => (
                                    <button
                                        key={range.value}
                                        onClick={() => {
                                            setDays(range.value);
                                            setShowDaysDropdown(false);
                                        }}
                                        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                                            days === range.value
                                                ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400'
                                                : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700'
                                        }`}
                                    >
                                        {range.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Refresh button */}
                    <button
                        onClick={fetchAnalytics}
                        className="p-2 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                        title="Refresh"
                    >
                        <RefreshCw className="w-4 h-4" />
                    </button>
                </div>
            </div>

            {/* Summary Stats */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <StatCard
                    title="Total Views"
                    value={summary?.totalViews?.toLocaleString() || '0'}
                    icon={Eye}
                    subtitle={`${summary?.totalGalleries || 0} galleries`}
                />
                <StatCard
                    title="Total Contacts"
                    value={summary?.totalContacts?.toLocaleString() || '0'}
                    icon={Mail}
                    changeType="positive"
                />
                <StatCard
                    title="Conversion Rate"
                    value={`${summary?.conversionRate?.toFixed(1) || '0'}%`}
                    icon={TrendingUp}
                    subtitle="Views to contacts"
                />
                <StatCard
                    title="Active Galleries"
                    value={summary?.totalGalleries || '0'}
                    icon={BarChart3}
                />
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                <DailyChart data={daily} />
                <DeviceBreakdown data={byDevice} />
            </div>

            {/* Bottom Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                <ReferrerBreakdown data={byReferrer} />
                <TopGalleries galleries={topGalleries} onViewGallery={handleViewGallery} />
            </div>
        </div>
    );
};

export default GalleryAnalytics;
